/* 
 * This file is part of Tibia Wheel.
 * Copyright (c) 2022 Maciej Sopyło
 * 
 * Tibia Wheel is free software: you can redistribute it and/or modify  
 * it under the terms of the GNU Lesser General Public License as published by  
 * the Free Software Foundation, version 3.
 *
 * Tibia Wheel is distributed in the hope that it will be useful, but 
 * WITHOUT ANY WARRANTY; without even the implied warranty of 
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the GNU 
 * General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License 
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React, { useContext } from 'react';
import { RootContext } from '../context';
import data from '../../data.yaml';
import { addInObject, iconCircle } from '../utils';

import { PerkSummary } from './PerkSummary';
import { PerkIcon } from './PerkIcon';
import { Widget } from './Widget';
import styles from './Summary.module.css';

export const Summary: React.FC = () => {
  const {
    perks,
    revelation,
    vocation,
  } = useContext(RootContext);

  const dedicationSum: Record<number, number> = { 0: 0, 1: 0, 3: 0, 8: 0 };
  const convictionSum: Record<number, number> = {};

  Object.entries(perks).forEach(([ key, points ]) => {
    const convictionIndex = data.perks.conviction[vocation][+key];
    const dedicationIndex = data.perks.dedication[+key];

    // special case for hp+mp so it doesn't show as a separate perk in the summary
    if (dedicationIndex === 2) {
      addInObject(dedicationSum, 0, points);
      addInObject(dedicationSum, 1, points);
    } else {
      addInObject(dedicationSum, dedicationIndex, points);
    }

    const circle = iconCircle(+key);
    if (points === data.pointsPerCircle[circle]) {
      addInObject(convictionSum, convictionIndex, 1);
    }
  });

  const dedication = [ 0, 1, 3, 8 ].map((key) => [ key, dedicationSum[key] ]);
  const conviction = Object.entries(convictionSum)
    .filter(([ key, value ]) => value > 0 && (+key < 37 || +key > 40))
    .sort(([ keyA ], [ keyB ]) => data.conviction[+keyA].name.localeCompare(data.conviction[+keyB].name));
  const revelationPerks = data.perks.revelation[vocation]
    .map((index, section) => ({ index, value: revelation[section] }))
    .sort((a, b) => data.revelation[a.index].name.localeCompare(data.revelation[b.index].name));
  const vessels = [
    { index: 37, label: 'Top Left' },
    { index: 38, label: 'Top Right' },
    { index: 39, label: 'Bottom Left' },
    { index: 40, label: 'Bottom Right' },
  ];
  const roman = (value: number) => [ '0', 'I', 'II', 'III' ][Math.min(3, value)];

  return <Widget className={styles.summary}>
      <span>Perks Summary</span>
      <div className={styles.group}>
        <h2>Dedication Perks</h2>
        <ul>
        { dedication.map(([ key, value ]) => <PerkSummary
            key={`dedication-${key}`}
            type="dedication"
            vocation={vocation}
            index={+key} value={value}/>)
        }
        </ul>
      </div>
      <div className={styles.group}>
        <h2>Conviction Perks</h2>
        {conviction.length === 0 && <p className={styles.none}>none</p>}
        {conviction.length > 0 && <ul>
        { conviction.map(([ key, value ]) => <PerkSummary
            key={`conviction-${key}`}
            type="conviction"
            index={+key}
            value={value}/>)
        }
        </ul>}
      </div>
      <div className={styles.group}>
        <h2>Revelation Perks</h2>
        <ul>
          {revelationPerks.map(({ index, value }) => <PerkSummary
            key={`revelation-${index}`}
            type="revelation"
            index={index}
            value={value}/>) }
        </ul>
      </div>
      <div className={styles.group}>
        <h2>Vessels</h2>
        <ul>
          {vessels.map(({ index, label }) => <li className={styles.vesselRow} key={index}>
            <PerkIcon type="conviction" icon={index}/>
            <span>{label}</span>
            <strong>VR {roman(convictionSum[index] || 0)}</strong>
          </li>)}
        </ul>
      </div>
      <p className={styles.note}>
        Note: The cooldown of a spell cannot be reduced to less than 50% of its base cooldown by any means.
      </p>
    </Widget>;
};
